import { KeyCode, RootRoute } from "@app/const";
import {
    FunctionType,
    ItemListView,
    NodeTypeName
} from "@app/types";
import { createUuid, modifierKey } from "@app/utilities";
import BaseComponent from "./BaseComponent";
import Button from "./Button";
import Dropdown from "./Dropdown";
import OldItemList from "./OldItemList";
import CustomScriptDialog from "../widgets/dialogs/CustomScriptDialog";

/**
 * @see {NodeTypeName}s where `customScript` should be used instead of `script`.
 */
const customScriptTypes = new Set([
    NodeTypeName.data,
    NodeTypeName.dataAccessCompletion
]);

export default class Function extends BaseComponent {
    get isDataNode() {
        const node = this.props.context;
        return customScriptTypes.has(node.props.nodeTypeName);
    }

    get isReadOnly() {
        const node = this.props.context;
        return (
            node.readOnly
            || this.props.disabled
            || this.props.functionType === FunctionType.reuseable
        );
    }

    /**
     * Function component for custom node; show corresponding component based on function type
     * @module Function
     * @param {Object} props React props
     */
    constructor(props) {
        super(props);
        this._onKeydown = this._onKeydown.bind(this);
        this.function = {};
        this.state = {
            isScriptOpen: false,
            scriptDialogTitle: ''
        };
    }

    /**
     * Set config proxy; change data model based on function type
     * @param {string} key
     * @param {Array|string} value
     */
    setConfig(key, value) {
        if (this.isDataNode) {
            this.props.context.props.config.customScript = value;
            this.updateContext(value);
            return;
        }
        if (this.props.functionType === FunctionType.custom) {
            delete this.function.ref;
        } else {
            if (key === "ref") {
                const f = window.apm.knowledgeBase.getFunctions(value);
                this.function.arguments = [];
                // Load argument definitions
                if (f.parameters) {
                    for (var i = 0; i < f.parameters.length; i++) {
                        this.function.arguments.push({
                            argumentName: f.parameters[i].key,
                            type: "value",
                            value: f.parameters[i].value ? f.parameters[i].value : "null",
                            varName: null
                        });
                    }
                }
            }
            delete this.function.script;
        }
        this.function[key] = value;
        this.updateContext(this.function);
        if (key === "ref") {
            this.forceUpdate();
        }
    }

    /**
     * Get current context value
     * @param {string} key
     * @returns {*}
     */
    getConfig(key) {
        return this.function[key];
    }

    componentWillMount() {
        if (this.props.value) {
            // create deep copy of props object
            this.function = _.cloneDeep(this.props.value);
        }
        document.addEventListener('keydown', this._onKeydown);
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this._onKeydown);
    }

    render() {
        if (!Object.values(FunctionType).includes(this.props.functionType)) {
            return null;
        }

        const labels = window.l.form;
        const nodeArguments = this.getConfig("arguments");
        const { isDataNode, isReadOnly } = this;
        const { isScriptOpen, scriptDialogTitle } = this.state;
        const isParentObject = this.props.context.id === this.props.context.props.parentComponentID
        // Context is using a script tied to a DesignModuleNode/DesignObject.
        const isCustomScript = this.props.functionType === FunctionType.custom;
        // Context is using a reusable DesignFunction.
        const isReusableScript = !isCustomScript;
        let argumentsTooltip = null;
        let reusableFunctionOptions = null;
        let selectedReusableFunctionIndex = null;

        if (isCustomScript) {
            argumentsTooltip = (isParentObject) ? (
                "Parent Custom Node input types/values do not propagate to children."
            ) : "Canvas Custom Node input types/values do not propagate to parent or siblings.";
        } else {
            reusableFunctionOptions = window.apm.knowledgeBase.getFunctions()?.map(({ id, name }) => {
                return { name, value: id };
            }) ?? [];
            selectedReusableFunctionIndex = (
                reusableFunctionOptions.findIndex(({ value }) => value === this.function.ref)
            );
        }

        return (
            <div>
                {(isCustomScript) ? null : (
                    <Dropdown key={createUuid()}
                        label={labels.function}
                        context={this}
                        valKey={"ref"}
                        optionName={"Select function"}
                        options={reusableFunctionOptions}
                        selection={selectedReusableFunctionIndex}
                        readOnly={this.props.context.readOnly}
                    />
                )}
                {(isDataNode) ? null : (
                    <OldItemList
                        context={this}
                        label={labels.arguments}
                        readOnly={this.props.disabled}
                        updateOnly={isReusableScript}
                        tooltip={argumentsTooltip}
                        valKey={"arguments"}
                        value={nodeArguments}
                        view={ItemListView.functionArgument}
                    />
                )}
                {(isDataNode) ? null : (
                    <OldItemList
                        context={this}
                        label={(isCustomScript) ? labels.write_output_to : labels.outputs}
                        readOnly={this.props.disabled}
                        valKey={"outputs"}
                        value={this.getConfig("outputs")}
                        view={ItemListView.return}
                    />
                )}
                {(isReusableScript && selectedReusableFunctionIndex === -1) ? null : (
                    <Button
                        label={(isReadOnly) ? labels.view_script : labels.edit_script}
                        title={`${(isReadOnly) ? 'View' : 'Edit'} Script (${modifierKey}E)`}
                        wide={true}
                        callback={() => this._handleOpenScript()}
                    />
                )}
                {(!isScriptOpen) ? null : (
                    <CustomScriptDialog
                        arguments={nodeArguments}
                        isDataNode={isDataNode}
                        node={this.props.context}
                        onClose={() => this.setState({ isScriptOpen: false, scriptDialogTitle: '' })}
                        onSave={(scriptKey, newScript) => this.setConfig(scriptKey, newScript)}
                        outputs={this.getConfig("outputs")}
                        readOnly={isReadOnly}
                        title={scriptDialogTitle}
                    />
                )}
            </div>
        );
    }

    /**
     * Listen for keydowns while rendered to open the script with the `meta + e` key binding.
     *
     * @param {KeyboardEvent} event of the key press.
     */
    _onKeydown(event) {
        const { code, metaKey } = event;
        if (
            code === KeyCode.e
            && metaKey
            && location.hash === `#${RootRoute.canvas}`
            && !this.state.isScriptOpen
        ) {
            this._handleOpenScript();
        }
    }

    _handleOpenScript() {
        const labels = window.l.form;
        const {
            context: node,
            functionType
        } = this.props;

        const scriptDialogTitle = (functionType === FunctionType.custom) ? (
            labels.custom_function + ((node.name) ? ` - ${node.name}` : "")
        ) : labels.reusable_function;

        this.setState({ isScriptOpen: true, scriptDialogTitle });
    }
}
